import { findTprInsuranceGridRowsByPackageNumber } from "@/configuration/groups/tpr/workflows/insurance-grid";
import {
  buildReferralAuthOnFileMessage,
  type ReferralAuthOnFileSnapshot,
} from "@/src/lib/prior-authorization/referral-auth-on-file";

export type TprReferralAuthLookup =
  | { need: "unknown"; packageNumber: string | null; planLabel: string | null }
  | { need: "authorization"; packageNumber: string; planLabel: string; gridSummary: string }
  | { need: "paper_referral"; packageNumber: string; planLabel: string; gridSummary: string }
  | { need: "not_required"; packageNumber: string; planLabel: string; gridSummary: string };

const NOT_REQUIRED_SHEET_VALUES = new Set(["Not Required", "N/A"]);
const OPEN_AUTHORIZATION_CTA =
  "Open Authorization to upload the PCP request letter to Athena, or to write a completed authorization.";
const OPEN_PAPER_REFERRAL_CTA = "Open Authorization to write a Referral object (PAPER, not required) to Athena.";
const VERIFY_MANUALLY = "Verify referral-authorization need manually before scheduling.";
const ADD_INSURANCE = "Add the patient's insurance in Athena, then return to this page.";

export const TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION =
  "Create the referral authorization in Athena manually. The provider is not in this department's local directory.";
const ATHENA_REFERRAL_AUTH_ID_NOTE_PATTERN = /Athena referral authorization ([A-Za-z0-9-]+)/;

export function formatTprReferralAuthNote(reviewedDate: string): string {
  return `Reviewed by Valerie Health ${reviewedDate}`;
}

export function explainTprReferralAuthNeed(packageNumber: string | null | undefined): TprReferralAuthLookup {
  const normalized = packageNumber?.trim() || null;

  if (!normalized) {
    return { need: "unknown", packageNumber: null, planLabel: null };
  }

  const rows = findTprInsuranceGridRowsByPackageNumber(normalized);
  const [row] = rows;

  if (!row) {
    return { need: "unknown", packageNumber: normalized, planLabel: null };
  }

  const planLabel = row.planNetwork ? `${row.insuranceName} / ${row.planNetwork}` : row.insuranceName;
  const referralRequired = agreedSheetValue(rows.map((match) => match.referralRequired));
  const authorizationRequired = agreedSheetValue(rows.map((match) => match.authorizationRequired));
  const needsVerification = rows.some((match) => match.needsVerification === "Yes");

  if (!needsVerification) {
    if (authorizationRequired === "Required") {
      return {
        need: "authorization",
        packageNumber: normalized,
        planLabel,
        gridSummary: formatGridSummary(referralRequired, authorizationRequired),
      };
    }

    if (
      referralRequired === "Required" &&
      authorizationRequired != null &&
      NOT_REQUIRED_SHEET_VALUES.has(authorizationRequired)
    ) {
      return {
        need: "paper_referral",
        packageNumber: normalized,
        planLabel,
        gridSummary: formatGridSummary(referralRequired, authorizationRequired),
      };
    }

    if (
      referralRequired != null &&
      authorizationRequired != null &&
      NOT_REQUIRED_SHEET_VALUES.has(referralRequired) &&
      NOT_REQUIRED_SHEET_VALUES.has(authorizationRequired)
    ) {
      return {
        need: "not_required",
        packageNumber: normalized,
        planLabel,
        gridSummary: formatGridSummary(referralRequired, authorizationRequired),
      };
    }
  }

  return { need: "unknown", packageNumber: normalized, planLabel };
}

export function formatAthenaReferralAuthCreatedNote(referralAuthId: string, alreadyExisted: boolean): string {
  return alreadyExisted
    ? `Found existing Athena referral authorization ${referralAuthId}.`
    : `Created Athena referral authorization ${referralAuthId}.`;
}

export function parseAthenaReferralAuthIdFromNote(note: string | null | undefined): string | null {
  const match = note?.match(ATHENA_REFERRAL_AUTH_ID_NOTE_PATTERN);

  return match?.[1] ?? null;
}

export function buildTprReferralAuthInstruction({
  authOnFile,
  createdAthenaReferralAuthId,
  hasWritableAthenaInsurance,
  lookup,
}: {
  authOnFile: ReferralAuthOnFileSnapshot;
  createdAthenaReferralAuthId?: string | null;
  hasWritableAthenaInsurance: boolean;
  lookup: TprReferralAuthLookup;
}): string {
  if (createdAthenaReferralAuthId) {
    return buildReferralAuthOnFileMessage(
      authOnFile,
      `Athena referral authorization ${createdAthenaReferralAuthId} is on the patient's insurance.`
    );
  }

  if (!hasWritableAthenaInsurance) {
    return ADD_INSURANCE;
  }

  if (lookup.need === "authorization") {
    const requiredLead = formatMatchedNeedSentence(lookup, "Referral authorization is required");

    return authOnFile.authorizationNumber
      ? buildReferralAuthOnFileMessage(authOnFile, requiredLead)
      : `${requiredLead} ${OPEN_AUTHORIZATION_CTA}`;
  }

  if (lookup.need === "paper_referral") {
    return `${formatMatchedNeedSentence(lookup, "Referral is required")} Authorization is not. ${OPEN_PAPER_REFERRAL_CTA}`;
  }

  if (lookup.need === "not_required") {
    return formatMatchedNeedSentence(lookup, "No referral authorization is required");
  }

  return formatUnknownMessage(lookup);
}

export function getTprReferralAuthOpenButtonTooltip({
  authOnFile,
  createdAthenaReferralAuthId,
  hasWritableAthenaInsurance,
  isSubmitting,
  lookup,
}: {
  authOnFile?: ReferralAuthOnFileSnapshot;
  createdAthenaReferralAuthId?: string | null;
  hasWritableAthenaInsurance: boolean;
  isSubmitting?: boolean;
  lookup: TprReferralAuthLookup;
}): string {
  if (createdAthenaReferralAuthId) {
    return `Athena referral authorization ${createdAthenaReferralAuthId} is already on the patient's insurance.`;
  }

  if (isSubmitting) {
    return "Finish updating the task before submitting the authorization.";
  }

  if (!hasWritableAthenaInsurance) {
    return ADD_INSURANCE;
  }

  if (lookup.need === "not_required") {
    return `Authorization is not required for ${lookup.planLabel}.`;
  }

  if (lookup.need === "paper_referral") {
    return "Writes a Referral object (PAPER, not required) to the patient's Athena insurance.";
  }

  if (authOnFile?.authorizationNumber) {
    return "Writes the completed referral authorization to the patient's Athena insurance.";
  }

  return "Writes a pending referral authorization to the patient's Athena insurance.";
}

export function formatTprReferralAuthSkipToast(
  reason: "self_pay" | "no_insurance" | "not_required" | "requirements_unresolved" | "provider_not_in_directory"
): {
  title: string;
  description: string;
} {
  switch (reason) {
    case "no_insurance":
      return {
        title: "Authorization not written",
        description: ADD_INSURANCE,
      };
    case "self_pay":
      return {
        title: "Authorization not required",
        description: "Self-pay referrals do not get an Athena referral authorization.",
      };
    case "not_required":
      return {
        title: "Authorization not required",
        description: "The confirmed insurance package does not require a referral authorization.",
      };
    case "requirements_unresolved":
      return {
        title: "Authorization not written",
        description: "Verify the insurance package's referral and authorization requirements manually.",
      };
    case "provider_not_in_directory":
      return {
        title: "Create authorization manually",
        description: TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION,
      };
    default: {
      const _exhaustive: never = reason;

      return _exhaustive;
    }
  }
}

export function formatTprReferralAuthWriteToast(
  write: "paper" | "authorization",
  alreadyExisted: boolean
): { title: string; description: string } {
  switch (write) {
    case "paper":
      return alreadyExisted
        ? {
            title: "Referral already in Athena",
            description: "This paper referral is already on the patient's insurance.",
          }
        : {
            title: "Referral written to Athena",
            description: "Athena now has a Referral object (PAPER, not required) on the patient's insurance.",
          };
    case "authorization":
      return alreadyExisted
        ? {
            title: "Authorization already in Athena",
            description: "This referral authorization is already on the patient's insurance.",
          }
        : {
            title: "Authorization written to Athena",
            description: "Athena now has the referral authorization on the patient's insurance.",
          };
    default: {
      const _exhaustive: never = write;

      return _exhaustive;
    }
  }
}

function agreedSheetValue(values: string[]): string | null {
  const unique = new Set(values.map((value) => value.trim()).filter(Boolean));

  if (unique.size !== 1) {
    return null;
  }

  return unique.values().next().value ?? null;
}

function formatGridSummary(referralRequired: string | null, authorizationRequired: string | null): string {
  const parts: string[] = [];

  if (referralRequired) {
    parts.push(`referral as ${referralRequired}`);
  }

  if (authorizationRequired) {
    parts.push(`authorization as ${authorizationRequired}`);
  }

  return parts.join(" and ");
}

function formatMatchedNeedSentence(
  lookup: Extract<TprReferralAuthLookup, { need: "authorization" | "paper_referral" | "not_required" }>,
  lead: string
): string {
  return `${lead} for ${lookup.planLabel} because the grid lists ${lookup.gridSummary}.`;
}

function formatUnknownMessage(lookup: Extract<TprReferralAuthLookup, { need: "unknown" }>): string {
  if (!lookup.packageNumber) {
    return `Could not find an Athena insurance package for this patient. ${VERIFY_MANUALLY}`;
  }

  if (!lookup.planLabel) {
    return `Athena insurance package ${lookup.packageNumber} is not on the contracted-plan grid. ${VERIFY_MANUALLY}`;
  }

  return `Unable to determine referral-authorization need for ${lookup.planLabel}. ${VERIFY_MANUALLY}`;
}
